import type { Address } from "viem";
import { arbitrum, base, bsc, ethereum, hyperevm, robinhood, type IdCadena } from "./chains";

/**
 * Configuracion de Keellabs por red. Portada de frontend/js/00-config.js, con las notas que costaron
 * dinero aprender — no son comentarios de adorno.
 */
export type RedKeel = {
  clave: "arbitrum" | "robinhood" | "hyperevm" | "bnb" | "base" | "ethereum";
  idCadena: IdCadena;
  etiqueta: string;
  /** RPC solo para `eth_getLogs`. Ver la nota de chains.ts. */
  rpcLogs?: string;
  /**
   * Cuantos bloques acepta ese RPC por consulta de eventos. Sin esto se pide la cadena entera de
   * una vez, que es lo que funciona en tres de las cuatro redes; donde no, hay que trocear.
   */
  ventanaLogs?: number;
  /**
   * El bloque donde se desplego la factory de esta red. Es el SUELO de cualquier busqueda de
   * eventos: ningun vault puede ser anterior a la factory que lo crea, asi que buscar por debajo es
   * gastar peticiones en tramos donde no hay nada que encontrar. En una red que hay que trocear,
   * esto es la diferencia entre tres consultas y doscientas.
   */
  bloqueFactory?: number;
  estable: Address;
  simboloEstable: string;
  /**
   * Gas fee MÍNIMO/sugerido al crear una piscina, en unidades del estable. El contrato solo
   * reembolsa `gasBudget / 10` por operación, así que para que el operador cobre el coste real de
   * gas de la red —y no lo subsidie de su bolsillo hasta secarse— la bolsa tiene que ser ≥ 10× ese
   * coste. En cadenas de gas caro (Robinhood: ~1,5 USDG por reajuste) eso son ~20; donde el gas es
   * barato (Arbitrum) con mucho menos sobra. Sin esto la gente dejaba 10 por defecto y el operador
   * perdía en cada operación. `undefined` = usa el mínimo genérico del modal.
   */
  gasMin?: number;
  /** Donde se CREAN los vaults nuevos. Desde el 2026-08-15, la factory con `MULTI_VAULT`: cada
   *  piscina es un vault nuevo y limpio, y se pueden tener varias en la misma pool. */
  factory: Address;
  /**
   * Todas las factories que se LISTAN. Regla del proyecto: AÑADIR, NUNCA SUSTITUIR. Las viejas
   * siguen vivas y sus vaults tambien; quitarlas de aqui le deja a su dueño sin la pantalla desde
   * la que sacar el dinero. Se hizo una excepcion el 2026-08-09 y solo tras comprobar vault por
   * vault que los 10 que existian estaban cerrados y a cero.
   */
  factories: readonly Address[];
  /**
   * El registro de referidos de esta red (`KeelReferidos`), o `undefined` mientras no este
   * desplegado. NO se inventa ni se copia de otra red: sin registro, la pantalla de invitaciones
   * dice que el reparto todavia no esta activo aqui, que es la verdad. Apuntar a una direccion de
   * otra cadena daria lecturas de un contrato ajeno sin fallar en ningun sitio visible.
   *
   * Tiene que ser LA MISMA que lleva grabada la logica de esa red (`KeelRebalancer.referidos`), y
   * la fuente de las dos es `script/ReferidosRed.sol`. Si se separan, la app enseña un estado y el
   * vault paga contra otro registro.
   */
  referidos?: Address;
  /** Pools que se ofrecen al crear. Curadas por ORACULO, no por tamaño: el vault exige que
   *  `observe(twapWindow)` responda, y hay pools con fondos cuya cardinalidad es 1 — abrir un
   *  vault ahi fallaria en el acto. */
  poolsPermitidas: readonly Address[];
  ficheroPools: string;
  /** Los vaults son clones EIP-1167 y no saben decir que firmas entienden. Se detecta por su
   *  implementacion. AÑADIR, NUNCA SUSTITUIR: quitar una deja sin botones a los vaults que
   *  nacieron con ella, y no falla nada visible. */
  /**
   * `KeelEntrada` de esta red: la puerta FIJA de alta. Con ella crear una piscina es 1 approve (a
   * este contrato verificado y etiquetado, nunca al vault recién nacido que Rabby/MetaMask marcan
   * como "EOA / confianza $0") + UNA transacción que crea el vault a tu nombre, deposita, abre y
   * deja los ajustes. `undefined` = flujo de siempre (createVault → approve → deposit → open …).
   *
   * SOLO se pone cuando la entrada esté desplegada Y `factory` (arriba) sea una factory que la
   * publique (`entrada()`) y que ella admita (`factoryOk`). La app lo comprueba en cadena antes de
   * usarla y, si no cuadra, vuelve sola al flujo viejo: un flag mal puesto no rompe la creación.
   */
  entrada?: Address;
  /** KeelAchicarQuoter de esta red (view): calcula el estable exacto para achicar el rango. */
  quoter?: Address;
  /**
   * Lógicas (`rebalancer`) cuyo `recenterTight` COMPRA el activo con el aporte (reinyección
   * promediada, 2026-10-28). Solo con ellas el modal de achicar calcula el aporte con
   * `lib/achicar.ts`: con una lógica anterior el aporte se quedaría en estable y el techo no se
   * movería, así que el modal pide actualizar la lógica antes. AÑADIR, NUNCA SUSTITUIR.
   */
  logicasAchicar?: readonly Address[];
  /**
   * Lógicas cuyo `achicarRango` con aporte 0 REDISTRIBUYE (2026-10-29): el estable propio compra el
   * activo y la banda sale con el capital. Con una anterior, aporte 0 dejaría el estable suelto.
   * AÑADIR, NUNCA SUSTITUIR.
   */
  logicasRedistribuir?: readonly Address[];
  /**
   * Lógicas con ORDEN PROGRAMADA de redistribución (2026-10-30): el dueño la programa y el robot la
   * ejecuta al llegar al gatillo. AÑADIR, NUNCA SUSTITUIR.
   */
  logicasOrden?: readonly Address[];
  implsConGestion: readonly Address[];
  /**
   * Fichero de EVENTOS ya indexados que sirve el VPS (`indexer-eventos.py`), relativo al raíz web
   * —igual que `ficheroPools`. Cuando está, `logsHistoricos` lee de aquí el histórico y solo escanea
   * on-chain el tramo posterior al último bloque indexado; sin él, escanea todo por ventanas.
   *
   * SOLO donde el RPC no acepta rangos grandes y el histórico crece más rápido que el tope de
   * ventanas: hoy solo BNB Chain, donde a los pocos días la cadena ya no cabría en 60 consultas y el
   * «ganado» se iría a blanco. Las otras tres redes recorren su histórico en una o pocas consultas y
   * no lo necesitan. Si el indexer se cae o se queda atrás, la app lo detecta y vuelve al escaneo
   * on-chain sola: es una optimización, no una dependencia.
   */
  indexadorEventos?: string;
};

export const REDES: Record<RedKeel["clave"], RedKeel> = {
  arbitrum: {
    clave: "arbitrum",
    idCadena: arbitrum.id,
    etiqueta: "Uniswap · Arbitrum",
    // 2026-10-13: arb1.arbitrum.io ahora RECHAZA eth_getLogs de mas de 100.000 bloques (~7 h en
    // Arbitrum): la consulta "desde 0" fallaba y las 20 ventanas de 9.000 no llegaban a los `Opened`
    // de hace semanas → «lleva corriendo —» en la portada (y sin historial/ganado). NO se pasa por el
    // proxy Alchemy (pago por uso, ya facturó de más): el histórico lo sirve el indexer de eventos
    // (keel-indexer-eventos-arb → eventos.arbitrum.json) y arb1 solo lee el tramo reciente.
    rpcLogs: "https://arb1.arbitrum.io/rpc",
    ventanaLogs: 99_000,
    bloqueFactory: 492_847_830, // factory más antigua de `factories` (0x61C6dE…)
    indexadorEventos: "eventos.arbitrum.json",
    estable: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    simboloEstable: "USDC",
    // 2026-09-11 REVERTIDO: la nueva 0xcebfd0ed (lote "2026-09-04 gas /1, gobierno frío") REVIERTE
    // `createVault` (verificado on-chain). La de abajo es la probada (2026-08-15 MULTI_VAULT).
    // 2026-09-30: factory NUEVA con la lógica de la BETA (la de 2026-08-15 llama initialize con 8
    // args y la impl beta espera 9 → setLogic imposible). La vieja sigue en `factories` para LEER sus vaults.
    factory: "0x3bfe98518FBE39F368395EdB82a1D6e8A581Fe39",
    logicasAchicar: ["0x42aC79f159236f86a639A659e65C29c6A6b78795", "0x57cE635aEE097c5AACd228D87674320032C211b6", "0x019E2aE4C4c3e830E8b8d312BD25D7b51e682F05"],
    logicasRedistribuir: ["0x57cE635aEE097c5AACd228D87674320032C211b6", "0x019E2aE4C4c3e830E8b8d312BD25D7b51e682F05"],
    logicasOrden: ["0x019E2aE4C4c3e830E8b8d312BD25D7b51e682F05"],
    factories: [
      "0x3bfe98518FBE39F368395EdB82a1D6e8A581Fe39", // 2026-09-30 lógica beta
      "0xDa877e3A5896dba00309684A5B40441f6A37e6e5",
      "0xcebfd0ed307e095b320d73fa83b770c693e164c9",
      "0x61C6dEc573505125EBc2b7e569250262b8dF33bC",
    ],
    referidos: "0x2f3cF0a4888ca8C7A8743236D9963e7f2F16969A", // 2026-08-18
    ficheroPools: "pools.json",
    poolsPermitidas: [
      "0xC6962004f452bE9203591991D15f6b388e09E8D0", // WETH/USDC 0,05%
      "0x2f5e87C9312fa29aed5c179E456625D79015299c", // WBTC/WETH 0,05%
      "0x5969EFddE3cF5C0D9a88aE51E47d721096A97203", // WBTC/USDT 0,05%
      "0x0E4831319A50228B9e450861297aB92dee15B44F", // WBTC/USDC 0,05%
      "0x641C00A822e8b671738d32a431a4Fb6074E5c79d", // WETH/USDT 0,05%
      "0xc473e2aEE3441BF9240Be85eb122aBB059A3B57c", // WETH/USDC 0,3%
    ],
    implsConGestion: [
      "0x1951F6BCE2261D302Ed4F51e5217b3F5E1BA5b6f", // 2026-09-30 lógica beta (colchón fijo, achicar, rescate, recupera por los 2 lados)
      "0xABfE0991CB509eDdf2E2BDAea93A96a3507b0F80", // 2026-09-09 rango asimétrico
      "0xf13ae3f10726F7B5375afC509a50783F07BbDfE8",
      "0x438b968Ab09f18E3A60bfDc10Be6448334dBE737",
      "0x37d69Ea07AFf8a5844730536E813585C6C64d215",
      "0x9c9287D45e1BEeB189a58752F5a1B02aB0a10DB2",
      "0x2f84BEb9B4A1b55556357de69aA06e9f526A84AB",
      "0x6222A399335567E6A1EE0dc5fd92d188da4e39ec", // 2026-08-12
      "0x7908A71cE235E298D65Ca7A01Cc9124313E0A3C0", // 2026-08-16, con objetivo de ganancia
      "0xd34c1d72f765D7E3C9413F373C70ea584f3c23fF", // 2026-08-18, con referidos
      "0xae2a62b0747c9c986c20b29cc08590840f67925b", // 2026-09-04 nuevo
    ],
  },

  robinhood: {
    clave: "robinhood",
    idCadena: robinhood.id,
    etiqueta: "Robinhood · Acciones",
    estable: "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168",
    simboloEstable: "USDG",
    // 2026-09-11 REVERTIDO a la factory probada: la nueva 0xd4be8c ("2026-09-04 gas /1,
    // gobierno frío") REVIERTE `createVault` con CUALQUIER pool (GME/WETH/CASHCAT, verificado por
    // simulacion on-chain) -> "Fallo en la Simulacion" desde la 1a firma. La de abajo es la que creo
    // los vaults vivos y simula OK. Dejar 0xd4be8c en `factories` para LEER si algun dia se abre uno.
    // 2026-09-30: factory NUEVA con la lógica de la BETA (la de 2026-08-15 llama initialize con 8
    // args y la impl beta espera 9 → setLogic imposible). La vieja sigue en `factories` para LEER sus vaults.
    factory: "0x041CA7A5F1113279edEC154B9B31Ee9380d55A70",
    logicasAchicar: ["0x5A7C8DCEC69193A4d30976B5103024bE930C4957", "0x698f02D098e850A7F96e590D9B243e2a1C313A9F", "0x2eDfFabf2D886C97B42D89C602F4A5487f961eEB"],
    logicasRedistribuir: ["0x698f02D098e850A7F96e590D9B243e2a1C313A9F", "0x2eDfFabf2D886C97B42D89C602F4A5487f961eEB"],
    logicasOrden: ["0x2eDfFabf2D886C97B42D89C602F4A5487f961eEB"],
    quoter: "0x061Bd3F71BB9389C3f4d126e6A1fFc84A85f277f", // KeelAchicarQuoter (view, sin gobierno; el mismo bytecode que la beta)
    factories: [
      "0x041CA7A5F1113279edEC154B9B31Ee9380d55A70", // 2026-09-30 lógica beta
      "0xc92bf423d730f1CA42F852d5Fc85467A10bCa572",
      "0xd4be8c553b7b26b3bae22b93498e035a4a923092",
      "0x2fA41b881d194628160d7f95f10442Dc6BC5e06F",
    ],
    referidos: "0x1b6D526c5041f58E47c843A063ecfF31e2C72Bc8", // 2026-08-18
    // 2026-10-01: la factory más antigua listada (0x2fA41b, 2026-08-09) nació en el bloque 32.219.528.
    // Sin esto los eventos se pedían desde el bloque 0 y el RPC público de Robinhood los rechaza
    // ("only 10000000 are allowed"): el historial de los vaults salía vacío en producción.
    bloqueFactory: 32_200_000,
    ventanaLogs: 9_000_000,
    ficheroPools: "pools.robinhood.json",
    poolsPermitidas: [
      "0x52e65B17fB6E5BA00Ed806f37Afcd2DaA50271Ca", // WETH/USDG 0,01%
      "0x69BfaF19C9f377BB306a89aEd9F6B07e2c1a8d9a", // WETH/USDG 0,05%
      // La de 0,05% es la de mas volumen pero quedo OCUPADA para siempre en la factory
      // (`vaultFor` no se puede borrar). Sin las otras dos no habria forma de tener GME con el
      // contrato nuevo — y no son un premio de consolacion: medido el 2026-08-09, la de 1% rinde
      // 44,8% contra 16,4%, con mas TVL ($462K vs $368K).
      "0xE2b46c905E12Ab8E2f864e4821a4325884C1B126", // GME/USDG  0,05%
      "0xE9713f453aDB9245B19559790c96F470a18F2fDF", // GME/USDG  1%
      "0x0A0675689C2Ad2a3aDe86539BCbD27B6c0764e9d", // GME/USDG  0,3%
      "0xd4EB21209C4D6093f80B5b84f5C45cc093EA14a3", // NVDA/USDG 0,05%
      "0x783C9bbB765047CFdD2b84b92b2Ca9F11D34b7Ed", // AAPL/USDG 0,3%
      "0xA43b424Bc609495AED4BCD88d654934b510B0aD9", // SPY/USDG  0,3%
      // SPCX/USDG (SpaceX, token de Robinhood 0x4a0E65A3EcceC6dBe60AE065F2e7bb85Fae35eEa).
      // Validado 2026-08-25 con check-memecoin.sh: el token es limpio —sin impuesto de transferencia
      // ni cooldown, y sin owner() que pueda mintear— y hay dos pools hondas con oraculo de sobra.
      // OJO con los impostores: el explorador lista una docena de "SPCX" (SpuceX, SpaceX Ipo,
      // ScammingPeople…). El bueno es el "Space Exploration Technologies Corp · Robinhood Token".
      // La de 0,05% es la de mas volumen: 606k USDG, cardinalidad 3100, mover el precio un 1% cuesta
      // ~87k USDG. La de 0,3% (538k, card 1500) va de respaldo. Las de 0,01% (fina) y 1% (seca) fuera.
      "0xc61284332117c3FB23A2A56cceFFD07F7aF60029", // SPCX/USDG 0,05%
      "0xEb07d9587eFD1778dFb9c385Ec43EF6d5F9fE401", // SPCX/USDG 0,3%
      // CRCL/USDG (Circle, token de Robinhood 0xdF0992E440dD0be65BD8439b609d6D4366bf1CB5).
      // Validado 2026-09-30 con check-memecoin.sh: token limpio (sin impuesto ni cooldown, sin
      // owner()). Solo la de 0,3% tiene fondos (~14,5k CRCL + 478k USDG, cardinalidad 3000, mover
      // el precio un 1% cuesta ~35k USDG); las de 0,05% y 1% estan SECAS (liquidez 0, card 1).
      // La CRCL/ETH 0,15% que enseña Uniswap es V4: Keel no la puede usar.
      "0x654E4143e82a5824445Ade0824351C2A9ACD95a8", // CRCL/USDG 0,3%
      // CASHCAT/USDG 1% — el memecoin de la cadena, y la primera pool que no es una accion.
      //
      // SOLO ESTA DE LAS TRES QUE EXISTEN. Las de 0,05% y 0,3% estan creadas pero SECAS (liquidez
      // activa 0, cardinalidad 1): abrir un vault ahi no fallaria al crearlo, fallaria al abrir la
      // posicion, que es peor porque el dinero ya esta dentro. Esta tiene $40k y cardinalidad 360,
      // asi que `observe(twapWindow)` responde y `_requireHonestPrice` puede hacer su trabajo.
      //
      // LO QUE HAY QUE SABER ANTES DE PONER DINERO, medido el 2026-08-16 sobre la pool real:
      // mover el precio un 1% cuesta 477 USDG y un 5% cuesta 2.360. Con el deslizamiento de
      // fabrica (0,5%) la venta de un vault de 1.000 USDG REVIERTE, porque mueve el precio 1,06%:
      // hay que abrirlo con `setGuards` al 3% o el cierre por objetivo no se ejecutaria nunca.
      "0x4B0c312fFbB068F6a0bEa128759E35d94B94D0E1",
    ],
    implsConGestion: [
      "0x90FaCA8e586D6b0A0B20d41B53f1A33cB4D5342f", // 2026-09-30 lógica beta (colchón fijo, achicar, rescate, recupera por los 2 lados)
      "0x823C7c304eA404fDAABF6f6dD016Bf9FdD5D36f8", // 2026-09-09 rango asimétrico
      "0xAd7f3B6C7D16e19A3284BE0cE14578296feA471A",
      "0x7908A71cE235E298D65Ca7A01Cc9124313E0A3C0", // los 3 vaults vivos nacieron con esta
      "0x9aeD3Eb3968A74102cEf11774A9d324eDA417175",
      "0xFA22fEe139026E67Abeb8e0ccF1e0b712FDCdf1E",
      "0x6392D6dE627F6D97701CDa5A433396Ca1BDb0d92",
      "0xBfb920d132b79a46BfDFd541dFE80dE0C4d593d0", // 2026-08-12
      "0x22DF589E4Ddc5424605ebc48A7E2E3F5EE78cf40", // 2026-08-16, con objetivo de ganancia
      "0x37a2B2C171245940aFc6e412404f1e79714Ca8CE", // 2026-08-18, con referidos
      "0x0055994d4082ca0aa09f16dfb88523b99ec8a79a", // 2026-09-04 nuevo
    ],
  },

  hyperevm: {
    clave: "hyperevm",
    idCadena: hyperevm.id,
    etiqueta: "PRJX · HyperEVM",
    rpcLogs: "https://rpc.purroofgroup.com",
    // NI ESTE RPC ACEPTA LA CADENA ENTERA. Contesta «Invalid parameters» a un `eth_getLogs` desde
    // el bloque 0, y hasta el 2026-08-19 eso no se notaba porque las busquedas de aqui solo querian
    // el ULTIMO evento y se resolvian con una ventana. El total ganado necesita recorrerlo TODO, y
    // sin estos dos numeros se quedaba sin historico.
    //
    // Medido el 2026-08-19 contra el propio RPC: acepta 100.000 bloques por consulta en ~300 ms
    // (el oficial, `rpc.hyperliquid.xyz`, rechaza hasta 1.000). Con el suelo puesto en la factory
    // mas antigua que se lista, el historico entero cabe en unas nueve consultas.
    ventanaLogs: 100_000,
    // Bloque de `0xb2AA23f1…`, la mas vieja de las dos factories de abajo (broadcast del 08-ago).
    bloqueFactory: 42_750_764,
    estable: "0xb88339CB7199b77E23DB6E890353E22632Ba630f",
    simboloEstable: "USDC",
    // 2026-09-11 REVERTIDO: la nueva 0xf02a1944 (lote "2026-09-04 gas /1, gobierno frío") REVIERTE
    // `createVault` (verificado on-chain). La de abajo es la probada (2026-08-15 MULTI_VAULT).
    // 2026-09-30: factory NUEVA con la lógica de la BETA (la de 2026-08-15 llama initialize con 8
    // args y la impl beta espera 9 → setLogic imposible). La vieja sigue en `factories` para LEER sus vaults.
    factory: "0xbE14445dcDab8d00d8497b3BA09CB2bA156353Fc",
    logicasAchicar: ["0x0bC8d0E200ed974C7287aFfD215f0E7862c36d26", "0xa1B1458aC60650647C57c89D76fcfbD343B32A60", "0x97813916C80853e1eb9232B80ab267B2bAAce4a7"],
    logicasRedistribuir: ["0xa1B1458aC60650647C57c89D76fcfbD343B32A60", "0x97813916C80853e1eb9232B80ab267B2bAAce4a7"],
    logicasOrden: ["0x97813916C80853e1eb9232B80ab267B2bAAce4a7"],
    factories: [
      "0xbE14445dcDab8d00d8497b3BA09CB2bA156353Fc", // 2026-09-30 lógica beta
      "0x309b918A4EBf5aB960B7787FE154d10229ED928b",
      "0xf02a1944b264dad67ed096c00d27c2b6d846faa9",
      "0xb2AA23f1664dB2AC87816ad69a2C19f217F57fc4",
    ],
    referidos: "0xc16c77A66F84b02Bf8A77C8e786DFF1d8b0f6d6d", // 2026-08-18
    ficheroPools: "pools.hyperevm.json",
    poolsPermitidas: [
      "0x6c9A33E3b592C0d65B3Ba59355d5Be0d38259285", // WHYPE/USDC 0,05%
      "0x422e586C906eb241f784B4F5a633c2C7e59A2F54", // WHYPE/USDC 0,3%
      "0xa4ABF3Edc4C470675c2dfd01b7E23a81a89cf24e", // UBTC/USDC  0,3%
      "0x78cc152a531dbde2f3fe7001ad659fa120fa893b", // UPUMP/WHYPE 0,3% (oráculo cardinalidad 360, seguro)
      "0x043538c4d9df365833b741ba2a3555787de947d3", // UPUMP/USDT0 0,3% (ancla en USD; oráculo provisionado a 100 el 2026-08-22)
      "0x0D6ECB912b6ee160e95Bc198b618Acc1bCb92525", // WHYPE/UBTC 0,3% (HYPE contra BTC; oráculo cardinalidad 360, seguro)
    ],
    implsConGestion: [
      "0x6392D6dE627F6D97701CDa5A433396Ca1BDb0d92", // 2026-09-30 lógica beta (colchón fijo, achicar, rescate, recupera por los 2 lados)
      "0x43eca80F03002Eaaf36f9235292703936889Cbfe", // 2026-09-09 rango asimétrico
      "0x390555ab8Bde8d8406820e5A7F981fC38a3dC189",
      "0x2af51d7E1BafA0885093fdeCCc96a9AD5E2c98b3",
      "0x9335bA057Fca911ccB31D9D81175F13A4d8BAfc6",
      "0xd91e58705729431Cf0A330b302D353DC81c39791",
      "0x7CfCEd5dFeF1884b057553B2b60F5d387005Cd3d",
      "0xadb3734Fe6bda27802869DAec9dc2BCBfF19898E", // 2026-08-12
      "0x4e2dB75D08dA7BEa8850482d2c79Fd307dBfcb51", // 2026-08-16, con objetivo de ganancia
      "0x3213963C6ffC87540AbEA49AD1feEB778bfe6325", // 2026-08-18, con referidos
      "0xb08a05001407e1ae997bce69c2f5e4ec90ff5fa1", // 2026-09-04 nuevo
    ],
  },
  bnb: {
    clave: "bnb",
    idCadena: bsc.id,
    etiqueta: "PancakeSwap · BNB Chain",
    // LOS EVENTOS NO PUEDEN IR POR `/rpc/bsc/`: el dataseed que hay detras rechaza `eth_getLogs`
    // SIEMPRE, incluso pidiendo mil bloques («limit exceeded»). Y tampoco por un proxy nuestro: los
    // proveedores que si sirven eventos limitan POR IP, asi que detras de nginx todos los usuarios
    // compartirian el limite del VPS y se agotaria para todos a la vez. Probado el 2026-08-18: el
    // proxy contestaba «You reached Public endpoint rate limit» a la primera. Va directo, cada
    // navegador con su IP.
    //
    // De ocho RPC publicos de BSC, este es el que mas rango acepta: 9.000 bloques por consulta. Por
    // eso las consultas de eventos van por ventanas (ver `logsConVentanas`).
    //
    // NO PONER AQUI publicnode: contesta «Archive requests require a personal token» a `eth_getLogs`
    // y a `eth_getTransactionReceipt` — el mismo proveedor y el mismo fallo que ya costo una
    // creacion de piscina en Arbitrum.
    // De ocho RPC publicos de BSC probados el 2026-08-18 con el volumen REAL de esta consulta
    // —los eventos de un vault, que son uno o ninguno—, este es el unico que responde: acepta hasta
    // 4.999 bloques por consulta y tarda ~3,7 s. Los demas o rechazan `eth_getLogs` de plano
    // (dataseed, meowrpc), o lo limitan a 25-50 bloques (blockrazor, 1rpc), o estan saturados
    // (drpc, blastapi). Y publicnode pide token de archivo, el mismo fallo que en Arbitrum.
    rpcLogs: "/rpc/bsc-logs/",
    // 4.900 y no 5.000, que es el maximo del proveedor: pedir justo el limite lo cruza en cuanto el
    // rango se calcula con un bloque de diferencia, y la respuesta es un error, no menos datos.
    ventanaLogs: 4900,
    // La factory se desplego aqui: por debajo de este bloque no hay nada que buscar.
    bloqueFactory: 116751353,
    estable: "0x55d398326f99059fF775485246999027B3197955",
    simboloEstable: "USDT",
    // OJO: el USDT de BSC tiene 18 decimales, no 6 como el USDC de Arbitrum o el USDG de Robinhood.
    // El vault lo lee del token, pero cualquier cifra escrita a mano en pantalla tiene que saberlo.
    // 2026-09-11 REVERTIDO: la nueva 0x0982a86f (lote "2026-09-04 gas /1, gobierno frío") REVIERTE
    // `createVault` (verificado on-chain). La de abajo es la probada.
    // 2026-09-30: factory NUEVA con la lógica de la BETA (la de 2026-08-15 llama initialize con 8
    // args y la impl beta espera 9 → setLogic imposible). La vieja sigue en `factories` para LEER sus vaults.
    factory: "0x1326b172e8419b6057e85fAA3605826b2DddAB60",
    logicasAchicar: ["0x38dab562ec4658c2Ad56cd088440a233fF43b1b1", "0x4CD557B5e7B7E9088b67f0e77e3e795afe4fb353", "0xA7b5f562dE7042902D299B312c56B2E143d1b498"],
    logicasRedistribuir: ["0x4CD557B5e7B7E9088b67f0e77e3e795afe4fb353", "0xA7b5f562dE7042902D299B312c56B2E143d1b498"],
    logicasOrden: ["0xA7b5f562dE7042902D299B312c56B2E143d1b498"],
    quoter: "0x80a68D760D787D46a917f92458Efc1EE52E646D9", // KeelAchicarQuoter (view, sin gobierno; el mismo bytecode que la beta)
    factories: [
      "0x1326b172e8419b6057e85fAA3605826b2DddAB60", // 2026-09-30 lógica beta
      "0x52dc92C7e3FdbD4fff7892dFc9DC7bc1d7a01ecf",
      "0x0982a86fc8e14653f263f1fe08d0f32227e383ff",
    ],
    referidos: "0x11AE454e8cEC52875C0aEA84C303837EAC4D342F", // 2026-08-18
    ficheroPools: "pools.bnb.json",
    // El histórico de eventos de BNB no cabe en el escaneo por ventanas (crece ~6 ventanas/día y el
    // tope son 60): se sirve pre-indexado. Ver `indexer-eventos.py` y `indexadorEventos` arriba.
    indexadorEventos: "eventos.bnb.json",
    // ES PANCAKESWAP, NO UNISWAP. Las pools de Uniswap V3 en esta misma cadena NO valen aqui: la
    // factory las rechaza (comprobado — revierte), porque valida contra la factory de Pancake.
    //
    // Curadas por ORACULO: las cinco responden a `observe(600)`, que es lo que el vault exige. Se
    // descartaron a proposito las de cardinalidad 150 o menos aunque tuvieran fondos —BTCB/USDT
    // 0,01% tiene 872k USDT dentro y se queda fuera— porque un TWAP de 10 minutos necesita
    // historia, y sin ella `_requireHonestPrice` no puede hacer su trabajo.
    poolsPermitidas: [
      "0x172fcD41E0913e95784454622d1c3724f546f849", // WBNB/USDT 0,01% — 7,5M USDT, card 2400
      "0x36696169C63e42cd08ce11f5deeBbCeBae652050", // WBNB/USDT 0,05% — 1,7M USDT, card 900
      "0x46Cf1cF8c69595804ba91dFdd8d6b960c9B0a7C4", // BTCB/USDT 0,05% — 5,8M USDT, card 2000
      "0xBe141893E4c6AD9272e8C04BAB7E6a10604501a5", // ETH/USDT  0,05% — 607k USDT, card 2400
      // XAUt/USDT 0,05% — ORO. Tether Gold, un token por onza troy de oro fisico custodiado.
      //
      // El 2026-08-09 se sondeo y NO HABIA NADA: PAXG tenia 50 tokens de supply en BSC y XAUt ni
      // existia en la red. Ahora sí, y con volumen de verdad. Medido el 2026-08-19 sobre la pool:
      //
      //   dentro         127,30 XAUt + 719.708 USDT  (~1,28 M$)   ·   volumen 24 h ~2,2 M$
      //   impacto        5.000 USDT mueven el precio 0,03% — el deslizamiento de fabrica sobra
      //   createVault    simulado contra la factory de BSC: acepta la pool
      //
      // OJO CON EL ORACULO, que es lo unico flojo. La cardinalidad es 60 y `observe()` llega a los
      // 15 minutos: el TWAP de 600 s que usa el vault entra, pero con poco margen. Si el volumen
      // sube y hay swaps en mas bloques seguidos, esa ventana se acorta y `_requireHonestPrice`
      // empezaria a revertir — el robot dejaria de reajustar sin que nada parezca roto. Se arregla
      // desde fuera y por unos centimos: `increaseObservationCardinalityNext(300)` sobre la pool,
      // que cualquiera puede llamar. Hasta entonces, esta es la unica de las cinco de oro que
      // responde: las de 0,25% y 1% tienen cardinalidad 1 y `observe` revierte con OLD.
      //
      // XAUt tiene 6 DECIMALES y el USDT de BSC 18. El estable es token1 (rama espejo, como QQQB).
      "0x83A0A8A723262651Ae9C54BBbA929F167443bC59",
      // QQQB/USDT 0,01% — el Nasdaq-100 tokenizado, 525k USDT, card 1300. Es la pool mas FINA que
      // ofrecemos: `tickSpacing` 1, asi que un mismo ±% cabe en muchos mas ticks y el robot
      // rebalancea mas a menudo. La bolsa de gas de estos vaults hay que dimensionarla pensando en
      // eso. Medido el 2026-08-18: una entrada de 5.000 USDT mueve el precio 0,0054%, y el token no
      // cobra comision al transferir.
      "0xe531fcb1F5a195de7608B9F4f9518544C2cdB693",
      // FXIon/USDT 0,25% — iShares China Large-Cap ETF tokenizado por Ondo (token
      // 0x9b8E987e6fEc8Cf1380C4dcA7071e2C7853AEEA1, 18 dec). Medido el 2026-09-30: ~2,37 M$ dentro
      // (18,5k FXIon + 1,74M USDT), card 2000, observe(600) OK, tickSpacing 50, sin comision al
      // transferir (fork). Mismo modelo que QQQB: proxy beacon de Ondo con `compliance` y
      // `tokenPauseManager` — el emisor puede pausar/bloquear, como en cualquier RWA regulado.
      // USDT es token0 (rama recoverToken0 = true).
      "0x8768C8969e177575e37435B8295aFa2DFB231C86",
    ],
    implsConGestion: [
      "0xEB907F0a2dFAA2724608A819FaFba45E2d584158", // 2026-09-30 lógica beta (colchón fijo, achicar, rescate, recupera por los 2 lados)
      "0x9AC888F87B707a15ecD3c64cFf1C89621505892A", // 2026-09-09 rango asimétrico
      "0xF4263810321f03C01abA727De0210c4BFB13fdB8", // 2026-08-18, la primera de esta red
      "0x2af51d7e1bafa0885093fdeccc96a9ad5e2c98b3", // 2026-09-04 nuevo
    ],
  },
  // Base (2026-09-04): Uniswap V3 sobre el L2 de Coinbase. Factory de Keel ya desplegada; sin
  // referidos ni indexer de eventos todavia (Base es L2 barato y su historico cabe en pocas
  // consultas, como Arbitrum). El estable es la USDC NATIVA de Circle, no la puenteada.
  base: {
    clave: "base",
    idCadena: base.id,
    // Base va sobre AERODROME SLIPSTREAM (no Uniswap): las pools se indexan por tickSpacing, se
    // mintea con sqrtPriceX96 y se swapea directo contra la pool. Es donde vive la liquidez de las
    // acciones tokenizadas B20 de Coinbase. La factory nace con slipstream=true.
    etiqueta: "Aerodrome · Base",
    rpcLogs: "https://mainnet.base.org",
    // Base CAPA `eth_getLogs` a 10.000 bloques por consulta (mensaje literal de mainnet.base.org
    // 2026-09-05) y no hay indexer de eventos todavia. Sin trocear, la ficha pedia `getLogs(0..latest)`
    // -> el RPC lo RECHAZA -> los paneles "El resultado" y "Que ha hecho el robot" se quedaban en
    // esqueleto para siempre. Con estos dos campos, `logsHistoricos` trocea en ventanas <=10k desde el
    // despliegue de la factory (bloque 50.903.214, ~2026-09-05). ⚠️ El proxy `/rpc/base/` (publicnode)
    // NO sirve para logs: pide "personal token" para archive; por eso rpcLogs se queda en base.org.
    ventanaLogs: 9900,
    bloqueFactory: 50_903_214,
    // INDEXER DE EVENTOS de Base (servicio keel-indexer-eventos-base → eventos.base.json), como BNB.
    // Sirve apertura ("lleva trabajando"), paneles e historial al INSTANTE y sin depender de que
    // base.org aguante la ráfaga de getLogs de la ficha (que a veces hipaba → "no encuentro la
    // apertura", TVL/paneles vacíos). Si el índice falta o se queda viejo (>300s), se cae al escaneo
    // on-chain troceado de siempre (ventanaLogs/bloqueFactory de arriba).
    indexadorEventos: "eventos.base.json",
    estable: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // USDC nativa (Circle), NO la puenteada
    simboloEstable: "USDC",
    // Stack Slipstream desplegado 2026-09-05 (slipstream=true, gobierno timelock frío, referidos).
    // Sustituye al stack Uniswap-Base anterior (0xf42638…/0x52dc92…), que quedó huérfano y NUNCA
    // tuvo vaults (verificado vaultsCount()==0) — por eso se puede quitar sin dejar a nadie fuera.
    factory: "0xa215f5c5b1A0Fc322376593F75fA422fcEB6712d",
    logicasAchicar: ["0xc34f67FD7bff5Fb0b88885dDE052B25c30411F07", "0xA6B3F22AA4b55762Af7163500DDa0ef9731bD8Eb", "0x0eF59be4a0F873D657E843f782F591d375cdeed3"],
    logicasRedistribuir: ["0xA6B3F22AA4b55762Af7163500DDa0ef9731bD8Eb", "0x0eF59be4a0F873D657E843f782F591d375cdeed3"],
    logicasOrden: ["0x0eF59be4a0F873D657E843f782F591d375cdeed3"],
    quoter: "0x80a68D760D787D46a917f92458Efc1EE52E646D9", // KeelAchicarQuoter (view)
    factories: ["0xa215f5c5b1A0Fc322376593F75fA422fcEB6712d"],
    referidos: "0xA16cb08995EB93f5e991a93e27cafaf2Eb7aD8d7", // 2026-09-05 (Slipstream)
    ficheroPools: "pools.base.json",
    // 7 acciones tokenizadas B20 contra USDC (tickSpacing 10) + ETH/BTC (WETH/cbBTC). Todas con
    // oráculo (cardinalidad >1) y liquidez verificadas on-chain 2026-09-05. createVault simula OK.
    poolsPermitidas: [
      "0x853F5f1B92b16714Fe6CDA67CAad0856B83C7ab9", // NVDA/USDC  — Nvidia
      "0xB1987CAD1682841b4b641d50E520777eC5Ab5542", // GOOGL/USDC — Google
      "0xEAF57753BC382E0324a1D43F72E7027705a2273E", // META/USDC  — Meta
      "0x7103eB3c9590d1281f7dc03b2A9EE27C39dF5D54", // MSFT/USDC  — Microsoft
      "0xd03Bc8C7F2FAedCe2aac81bF0444AEA08Ea06E9b", // AMZN/USDC  — Amazon
      "0x469337fDcc5E8f38e2E4B670B04F57865D13a7BB", // TSLA/USDC  — Tesla
      "0x8b27f626ab668197000BC722A1012022CAeD10E2", // MSTR/USDC  — MicroStrategy
      "0x42d4a22CaD0F5a49681a5715cE994Af73A43B76b", // WETH/cbBTC — ETH/BTC
    ],
    implsConGestion: [
      "0x273C9691e1eb5bDDE100EFF07432F705E9BE1586", // 2026-09-30 lógica beta (tras el setLogic del timelock frío)
      "0xF7e6EE816960E8d80f34F86860e7013bE4818617", // 2026-09-09 rango asimétrico (Slipstream)
      "0x1E2c70bbEB3A156443B6ECBa23105FedD74a71a8", // 2026-09-05, Slipstream
    ],
  },
  // ETHEREUM mainnet (Uniswap V3), desde 2026-09-30: stack de PRODUCCIÓN propio con la lógica de la
  // beta (script/DeployKeelEthProd.s.sol en keel-beta), gobierno = timelock 24 h de la misma wallet
  // que las otras redes. El gas de L1 vive <1 gwei (~0,13 $/reajuste).
  ethereum: {
    clave: "ethereum",
    idCadena: ethereum.id,
    etiqueta: "Uniswap · Ethereum",
    rpcLogs: "https://ethereum-rpc.publicnode.com",
    ventanaLogs: 9900,
    estable: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC de Circle
    simboloEstable: "USDC",
    gasMin: 25,
    bloqueFactory: 26_092_256, // despliegue del stack de prod: suelo de búsqueda de eventos
    factory: "0xcc5E6AE42dD0D3DB2aD9016994cFEf460d05E781",
    logicasAchicar: ["0xadb3734Fe6bda27802869DAec9dc2BCBfF19898E", "0x3B733a2a77097C70DEb1efdc61E76Bed98340573"],
    logicasRedistribuir: ["0x3B733a2a77097C70DEb1efdc61E76Bed98340573"],
    logicasOrden: ["0x3B733a2a77097C70DEb1efdc61E76Bed98340573"],
    factories: ["0xcc5E6AE42dD0D3DB2aD9016994cFEf460d05E781"],
    referidos: "0xdc8C7754615adb237c1a644314401a86355F1977",
    quoter: "0x9a9d75a5Fb05Db4601F152109AA0293F8c6aCE5e",
    ficheroPools: "pools.ethereum.json",
    // Las de la beta: verificadas on-chain el 2026-09-17, liquidez real (no el fee tier más barato).
    poolsPermitidas: [
      "0x88e6A0c2dDD26FEEb64F039a2c41296FcB3f5640", // WETH/USDC 0,05% — la más profunda de mainnet
      "0x4e68Ccd3E89f51C3074ca5072bbAC773960dFa36", // WETH/USDT 0,3%
      "0xD0fC8bA7E267f2bc56044A7715A489d851dC6D78", // UNI/USDC  0,3%
      "0x3470447f3CecfFAc709D3e783A307790b0208d60", // UNI/USDT  0,3%
      "0xdceaf5d0E5E0dB9596A47C0c4120654e80B1d706", // AAVE/USDC 0,3%
      "0x4D1Ad4A9e61Bc0E5529d64F38199cCFca56f5a42", // AAVE/USDT 0,3%
    ],
    implsConGestion: [
      "0x11AE454e8cEC52875C0aEA84C303837EAC4D342F", // 2026-09-30 lógica beta
    ],
  },
};

/**
 * Implementaciones JUBILADAS. Es una LISTA NEGRA, no una lista blanca de la actual: con lista
 * blanca, cada `setLogic` futuro esconderia de golpe los vaults de la generacion anterior, que si
 * tienen dinero dentro. Ocultar un vault es quitarle a su dueño la unica pantalla desde la que
 * sacar el dinero, asi que solo se añade aqui a mano y sabiendo lo que hay dentro.
 */
export const IMPLS_OCULTAS: Record<RedKeel["clave"], readonly Address[]> = {
  arbitrum: ["0xf13ae3f10726F7B5375afC509a50783F07BbDfE8"],
  robinhood: ["0xAd7f3B6C7D16e19A3284BE0cE14578296feA471A"],
  hyperevm: ["0x390555ab8Bde8d8406820e5A7F981fC38a3dC189"],
  // BNB Chain estrena red: no hay ninguna generacion anterior que ocultar todavia.
  bnb: [],
  // Base estrena red: no hay ninguna generacion anterior que ocultar todavia.
  base: [],
  // Ethereum estrena red: no hay ninguna generacion anterior que ocultar todavia.
  ethereum: [],
};

/** Redes que la app muestra y escanea: en producción, todas las de `REDES`. */
export const redesActivas = (): RedKeel[] => Object.values(REDES);

export const porIdCadena = (id: number): RedKeel | undefined =>
  Object.values(REDES).find((r) => r.idCadena === id);

export const esIgual = (a?: string, b?: string) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();
